import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "../useAuth";
import { dbLoadUserCourses, dbLoadQuestionBank, dbSaveQuestionToBank, dbLoadTags, dbCreateTag, dbAddTagToQuestion, dbRemoveTagFromQuestion, dbDeleteQuestion, dbUpdateQuestion } from "../lib/db";
import { Lock, BookOpen, Pencil, Trash2 } from "lucide-react";
import { stripFormatting } from "../lib/formatting";
import { PageHead, ConfirmDialog, QuestionImages, RichText } from "../components/common";
import { RichTextField } from "../components/RichTextField";
import { T } from "../lib/i18n";

// ─────────────────────────────────────────
//  QUESTION BANK SCREEN
// ─────────────────────────────────────────
function QuestionBankScreen({ onBack, lang, G, css }) {
  const { user } = useAuth();
  const t = T[lang];
  const [questions, setQuestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterTag, setFilterTag] = useState(null);
  const [filterScope, setFilterScope] = useState("all");
  const [showForm, setShowForm] = useState(false); // new question form
  const [showTagForm, setShowTagForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [myCourseInstances, setMyCourseInstances] = useState([]);

  // Form state for new/edit question
  const emptyQ = { question_text: "", correct_answer: "A", explanation: "", options: { A: "", B: "", C: "", D: "" }, scope: "private", course_id: null, program_id: null, org_id: null, selectedTags: [] };
  const [formQ, setFormQ] = useState(emptyQ);

  // Tag form
  const [newTagName, setNewTagName] = useState("");

  const flash = (text, type = "ok") => { setMsg({ text, type }); setTimeout(() => setMsg(null), 3000); };

  const loadAll = async () => {
    setLoading(true);
    const [q, t, ci] = await Promise.all([
      dbLoadQuestionBank(user.id),
      dbLoadTags(user.id),
      dbLoadUserCourses(user.id),
    ]);
    setQuestions(q);
    setTags(t);
    setMyCourseInstances(ci);
    setLoading(false);
  };

  useEffect(() => { if (user) loadAll(); }, [user]);

  const labels = ["A", "B", "C", "D", "E", "F"];

  const saveQuestion = async () => {
    if (!formQ.question_text.trim()) { flash(t.qbErrNoText, "err"); return; }
    if (!formQ.correct_answer) { flash(t.qbErrNoCorrect, "err"); return; }
    setSaving(true);

    if (editingQuestion) {
      // Update existing
      await dbUpdateQuestion(editingQuestion.id, {
        question_text: formQ.question_text,
        correct_answer: formQ.correct_answer,
        explanation: formQ.explanation,
        options: formQ.options,
      });
      // Update visibility
      await supabase.from("question_visibility").upsert({
        question_id: editingQuestion.id,
        scope: formQ.scope,
        course_id: formQ.course_id || null,
        program_id: formQ.program_id || null,
        org_id: formQ.org_id || null,
      });
      flash(t.qbQuestionUpdated);
    } else {
      // Create new
      const data = await dbSaveQuestionToBank(
        { text: formQ.question_text, options: formQ.options, correctAnswer: formQ.correct_answer, rationale: formQ.explanation },
        user.id,
        null,
        formQ.course_id,
        formQ.program_id,
        formQ.org_id,
      );
      if (!data) { flash(t.qbErrSave, "err"); setSaving(false); return; }

      // Add tags
      for (const tagId of (formQ.selectedTags || [])) {
        await dbAddTagToQuestion(data.id, tagId);
      }
      flash(t.qbQuestionSaved);
    }
    setSaving(false);
    setShowForm(false);
    setEditingQuestion(null);
    setFormQ(emptyQ);
    loadAll();
  };

  // Bekräftelse i appens design i stället för native window.confirm()
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const deleteQuestion = (id) => setConfirmDeleteId(id);
  const doDeleteQuestion = async (id) => {
    setConfirmDeleteId(null);
    await dbDeleteQuestion(id);
    flash(t.qbQuestionDeleted);
    loadAll();
  };

  const toggleTag = async (questionId, tagId, hasTag) => {
    if (hasTag) await dbRemoveTagFromQuestion(questionId, tagId);
    else await dbAddTagToQuestion(questionId, tagId);
    loadAll();
  };

  const createTag = async () => {
    if (!newTagName.trim()) return;
    await dbCreateTag(newTagName.trim(), user.id);
    setNewTagName("");
    setShowTagForm(false);
    loadAll();
    flash(t.qbTagCreated);
  };

  const startEdit = (q) => {
    const vis = q.question_visibility?.[0] || {};
    setFormQ({
      question_text: q.question_text || "",
      correct_answer: q.correct_answer || "A",
      explanation: q.explanation || "",
      options: q.options || { A: "", B: "", C: "", D: "" },
      scope: vis.scope || "private",
      course_id: vis.course_id || null,
      program_id: vis.program_id || null,
      org_id: vis.org_id || null,
      selectedTags: (q.question_tags || []).map(qt => qt.tag_id),
    });
    setEditingQuestion(q);
    setShowForm(true);
  };

  const scopeLabel = { private: t.qbScopePrivate, course: t.qbScopeCourse, program: t.qbScopeProgram, org: t.qbScopeOrg };

  // Filter
  const filtered = questions.filter(q => {
    const matchSearch = !search || stripFormatting(q.question_text).toLowerCase().includes(search.toLowerCase());
    const matchTag = !filterTag || (q.question_tags || []).some(qt => qt.tag_id === filterTag);
    const matchScope = filterScope === "all" || (q.question_visibility?.[0]?.scope === filterScope);
    return matchSearch && matchTag && matchScope;
  });

  const numOptions = Object.keys(formQ.options || {}).length;

  return (
    <div style={css.page}>

      {msg && (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 9999, background: msg.type === "err" ? G.red : G.correct, color: "#fff", borderRadius: 12, padding: "10px 18px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" }}>
          {msg.text}
        </div>
      )}

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "36px 20px" }}>
        <PageHead
          G={G}
          title={t.qbTitle}
          sub={t.qbSubtitle}
          action={
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => setShowTagForm(true)} style={{ background: "transparent", border: `1px solid ${G.border}`, borderRadius: 8, color: G.muted, fontSize: 13, padding: "8px 14px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>+ {t.qbTag}</button>
              <button onClick={() => { setFormQ(emptyQ); setEditingQuestion(null); setShowForm(true); }} style={{ background: G.gold, color: G.onGold, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, padding: "9px 16px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>+ {t.qbNewQuestion}</button>
            </div>
          }
        />
        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <input
            style={{ ...css.input, flex: 1, minWidth: 200, padding: "8px 12px" }}
            placeholder={t.qbSearchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={{ ...css.input, width: "auto", padding: "8px 12px" }} value={filterScope} onChange={e => setFilterScope(e.target.value)}>
            <option value="all">{t.qbFilterAllScopes}</option>
            <option value="private">{t.qbFilterPrivate}</option>
            <option value="course">{t.qbFilterCourse}</option>
          </select>
        </div>

        {/* Tag filter pills */}
        {tags.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
            <button
              onClick={() => setFilterTag(null)}
              style={{ padding: "4px 12px", borderRadius: 20, border: `1px solid ${filterTag === null ? G.gold : G.border}`, background: filterTag === null ? `${G.gold}20` : "transparent", color: filterTag === null ? G.gold : G.muted, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
            >
              {t.qbAllTags}
            </button>
            {tags.map(tag => (
              <button
                key={tag.id}
                onClick={() => setFilterTag(filterTag === tag.id ? null : tag.id)}
                style={{ padding: "4px 12px", borderRadius: 20, border: `1px solid ${filterTag === tag.id ? G.gold : G.border}`, background: filterTag === tag.id ? `${G.gold}20` : "transparent", color: filterTag === tag.id ? G.gold : G.muted, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}
              >
                {tag.name}
              </button>
            ))}
          </div>
        )}

        {loading && <p style={{ color: G.muted, textAlign: "center", padding: 40 }}>{t.loading}</p>}

        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: "center", color: G.muted, padding: 48, background: G.surface, borderRadius: 16, border: `1px solid ${G.border}` }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>{questions.length === 0 ? t.qbEmpty : t.qbNoMatch}</p>
            {questions.length === 0 && <p style={{ fontSize: 14 }}>{t.qbEmptyHint}</p>}
          </div>
        )}

        {/* Question list */}
        <div style={{ display: "grid", gap: 10 }}>
          {filtered.map(q => {
            const vis = q.question_visibility?.[0];
            const qTags = (q.question_tags || []).map(qt => qt.tags).filter(Boolean);
            const isExpanded = expandedQuestion === q.id;

            return (
              <div key={q.id} style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.2s" }}>
                {/* Question header */}
                <div
                  style={{ padding: "14px 18px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 12 }}
                  onClick={() => setExpandedQuestion(isExpanded ? null : q.id)}
                >
                  {/* Correct answer badge */}
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: 6, background: `${G.correct}20`, color: G.correct, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14, flexShrink: 0, marginTop: 2 }}>
                    {q.correct_answer || "?"}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: G.text, fontSize: 14, margin: 0, lineHeight: 1.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: isExpanded ? "normal" : "nowrap" }}>
                      {q.question_text ? <RichText text={q.question_text} /> : <span style={{ color: G.muted, fontStyle: "italic" }}>{t.qbNoQuestionText}</span>}
                    </p>
                    <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {vis && (
                        <span style={{ fontSize: 12, color: G.muted, background: G.surface2, borderRadius: 4, padding: "1px 7px" }}>{scopeLabel[vis.scope] || vis.scope}</span>
                      )}
                      {qTags.map(tag => (
                        <span key={tag.id} style={{ fontSize: 12, color: G.muted, background: G.surface2, border: `1px solid ${G.border}`, borderRadius: 12, padding: "1px 8px" }}>{tag.name}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                    <button onClick={e => { e.stopPropagation(); startEdit(q); }} style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: `1px solid ${G.border}`, borderRadius: 6, color: G.muted, fontSize: 12, padding: "4px 10px", cursor: "pointer" }}><Pencil size={12} />{t.qbEdit}</button>
                    <button onClick={e => { e.stopPropagation(); deleteQuestion(q.id); }} aria-label={t.qbDeleteQuestionAria} title={t.qbDeleteQuestionAria} style={{ display: "inline-flex", alignItems: "center", background: "none", border: `1px solid ${G.red}40`, borderRadius: 6, color: G.red, padding: "4px 8px", cursor: "pointer" }}><Trash2 size={13} /></button>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${G.border}` }}>
                    {/* Figurer — frågor vars innehåll ligger i en bild */}
                    {q.images?.length > 0 && (
                      <div style={{ marginTop: 12, maxWidth: 420 }}>
                        <QuestionImages images={q.images} G={G} lang={lang} maxHeight={260} />
                      </div>
                    )}
                    {/* Answer options */}
                    {q.options && Object.keys(q.options).length > 0 && (
                      <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
                        {labels.filter(l => q.options[l] !== undefined && q.options[l] !== "").map(lbl => (
                          <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 13, fontWeight: 700, minWidth: 20, color: lbl === q.correct_answer ? G.correct : G.muted }}>{lbl}{lbl === q.correct_answer ? " ✓" : ""}</span>
                            <span style={{ fontSize: 14, color: G.sub }}>{q.options[lbl]}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Explanation */}
                    {q.explanation && (
                      <div style={{ marginTop: 12, padding: "10px 14px", background: `${G.gold}10`, borderRadius: 8, borderLeft: `3px solid ${G.gold}` }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: G.gold, margin: "0 0 4px" }}>{t.qbRationale}</p>
                        <p style={{ fontSize: 14, color: G.sub, margin: 0, lineHeight: 1.5 }}><RichText text={q.explanation} /></p>
                      </div>
                    )}
                    {/* Tag management */}
                    <div style={{ marginTop: 12 }}>
                      <p style={{ fontSize: 12, fontWeight: 700, color: G.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>{t.qbTags}</p>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {tags.map(tag => {
                          const hasTag = (q.question_tags || []).some(qt => qt.tag_id === tag.id);
                          return (
                            <button
                              key={tag.id}
                              onClick={() => toggleTag(q.id, tag.id, hasTag)}
                              style={{ padding: "3px 10px", borderRadius: 12, border: `1px solid ${hasTag ? G.gold : G.border}`, background: hasTag ? `${G.gold}18` : "transparent", color: hasTag ? G.gold : G.muted, fontSize: 12, cursor: "pointer" }}
                            >
                              {hasTag ? "✓ " : ""}{tag.name}
                            </button>
                          );
                        })}
                        {tags.length === 0 && <span style={{ color: G.muted, fontSize: 13, fontStyle: "italic" }}>{t.qbNoTagsYet}</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── NEW / EDIT QUESTION MODAL ── */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 10000 }} onClick={() => { setShowForm(false); setEditingQuestion(null); }}>
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 18, padding: 28, maxWidth: 560, width: "100%", maxHeight: "92vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...css.heading, fontSize: 18, marginBottom: 20 }}>{editingQuestion ? t.qbEditQuestion : t.qbNewQuestion}</h3>

            {/* Question text */}
            <label style={css.label}>{t.qbQuestionTextLabel}</label>
            <RichTextField value={formQ.question_text} onChange={v => setFormQ(f => ({ ...f, question_text: v }))} placeholder={t.qbQuestionTextPlaceholder} ariaLabel={t.qbQuestionTextLabel} minHeight={80} style={{ marginBottom: 16 }} autoFocus G={G} css={css} lang={lang} />

            {/* Answer options */}
            <label style={css.label}>{t.qbOptionsLabel}</label>
            <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
              {["A", "B", "C", "D", "E"].map(lbl => (
                <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    onClick={() => setFormQ(f => ({ ...f, correct_answer: lbl }))}
                    title={t.qbSetAsCorrect}
                    style={{ fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14, minWidth: 28, textAlign: "center", padding: "3px 6px", borderRadius: 6, cursor: "pointer", color: formQ.correct_answer === lbl ? G.correct : G.muted, background: formQ.correct_answer === lbl ? G.correct + "20" : G.surface2, transition: "all 0.15s", userSelect: "none" }}
                  >
                    {lbl}{formQ.correct_answer === lbl ? " ✓" : ""}
                  </span>
                  <input style={{ ...css.input, flex: 1, fontSize: 14, padding: "7px 10px" }} placeholder={t.qbOptionPlaceholder.replace("{label}", lbl)} value={formQ.options?.[lbl] || ""} onChange={e => setFormQ(f => ({ ...f, options: { ...f.options, [lbl]: e.target.value } }))} />
                </div>
              ))}
            </div>
            <p style={{ color: G.muted, fontSize: 12, marginBottom: 16 }}>{t.qbCorrectHint}</p>

            {/* Explanation */}
            <label style={css.label}>{t.qbRationaleOptional}</label>
            <RichTextField value={formQ.explanation} onChange={v => setFormQ(f => ({ ...f, explanation: v }))} placeholder={t.qbRationalePlaceholder} ariaLabel={t.qbRationaleOptional} minHeight={60} style={{ marginBottom: 16 }} G={G} css={css} lang={lang} />

            {/* Synlighet — en fråga hör till sin kurskontext (eller är privat).
                Program- och organisationsnivå togs bort: en fråga skriven för en
                kurs är sällan meningsfull utanför den. Segmenterade knappar i
                stället för en select, så alternativen syns direkt. */}
            <label style={css.label}>{t.qbVisibility}</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              {[
                { id: "private", label: t.qbScopePrivateOnlyMe, Icon: Lock },
                { id: "course", label: t.qbScopeCourse, Icon: BookOpen },
              ].map(({ id, label, Icon }) => {
                const active = formQ.scope === id;
                return (
                  <button key={id} type="button"
                    onClick={() => setFormQ(f => ({ ...f, scope: id, course_id: null, program_id: null, org_id: null }))}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                      background: active ? G.gold + "18" : "transparent",
                      border: `1.5px solid ${active ? G.gold : G.border}`,
                      borderRadius: 10, color: active ? G.gold : G.muted,
                      fontSize: 14, fontWeight: active ? 600 : 500,
                      padding: "10px 12px", cursor: "pointer",
                      fontFamily: "'DM Sans',sans-serif", transition: "all 0.15s",
                    }}>
                    <Icon size={15} />
                    {label}
                  </button>
                );
              })}
            </div>

            {formQ.scope === "course" && myCourseInstances.length > 0 && (
              <>
                <label style={css.label}>{t.qbCourseLabel}</label>
                <select style={{ ...css.input, marginBottom: 12 }} value={formQ.course_id || ""} onChange={e => setFormQ(f => ({ ...f, course_id: e.target.value || null }))}>
                  <option value="">{t.qbSelectCourse}</option>
                  {myCourseInstances.map(inst => (
                    <option key={inst.id} value={inst.course_id}>{inst.courses?.name} · {inst.name}</option>
                  ))}
                </select>
              </>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <>
                <label style={css.label}>{t.qbTags}</label>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
                  {tags.map(tag => {
                    const selected = (formQ.selectedTags || []).includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => setFormQ(f => ({ ...f, selectedTags: selected ? f.selectedTags.filter(id => id !== tag.id) : [...(f.selectedTags || []), tag.id] }))}
                        style={{ padding: "4px 12px", borderRadius: 12, border: `1px solid ${selected ? G.gold : G.border}`, background: selected ? `${G.gold}18` : "transparent", color: selected ? G.gold : G.muted, fontSize: 13, cursor: "pointer" }}
                      >
                        {selected ? "✓ " : ""}{tag.name}
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <button style={{ ...css.btnPrimary, width: "100%" }} onClick={saveQuestion} disabled={saving}>{saving ? t.qbSaving : editingQuestion ? t.qbSaveChanges : t.qbSaveQuestion}</button>
            <button style={css.btnSecondary} onClick={() => { setShowForm(false); setEditingQuestion(null); }}>{t.cancel}</button>
          </div>
        </div>
      )}

      {/* ── TAG FORM MODAL ── */}
      {showTagForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 10000 }} onClick={() => setShowTagForm(false)}>
          <div style={{ background: G.surface, border: `1px solid ${G.border}`, borderRadius: 16, padding: 28, maxWidth: 360, width: "100%" }} onClick={e => e.stopPropagation()}>
            <h3 style={{ ...css.heading, fontSize: 16, marginBottom: 20 }}>{t.qbNewTag}</h3>
            <label style={css.label}>{t.qbTagNameLabel}</label>
            <input style={{ ...css.input, marginBottom: 12 }} placeholder={t.qbTagNamePlaceholder} value={newTagName} onChange={e => setNewTagName(e.target.value)} autoFocus onKeyDown={e => e.key === "Enter" && createTag()} />
            {/* Taggarna är enfärgade — färgvalet tillförde inget och gjorde
                listan spretig. Bara en förhandsvisning av namnet kvar. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <span style={{ padding: "4px 14px", borderRadius: 12, background: G.surface2, color: G.muted, border: `1px solid ${G.border}`, fontSize: 14 }}>{newTagName || t.qbTagPreview}</span>
            </div>
            <p style={{ fontSize: 12, color: G.muted, lineHeight: 1.5, marginBottom: 16 }}>{t.qbTagPrivateHint}</p>
            <button style={{ ...css.btnPrimary, width: "100%" }} onClick={createTag}>{t.qbCreateTag}</button>
            <button style={css.btnSecondary} onClick={() => setShowTagForm(false)}>{t.cancel}</button>
          </div>
        </div>
      )}
      {confirmDeleteId && (
        <ConfirmDialog
          text={t.qbConfirmDelete}
          lang={lang}
          G={G}
          css={css}
          onCancel={() => setConfirmDeleteId(null)}
          onConfirm={() => doDeleteQuestion(confirmDeleteId)}
        />
      )}
    </div>
  );
}

export { QuestionBankScreen };
